/**
 * EnvironmentsClient - HTTP wrapper around the environment endpoints.
 *
 * Two prefixes, because there are two questions with two different gates:
 *
 * - `/api/admin/environments*` — "who has a workspace here, and may I take it
 *   away?", gated `u.view`/`u.edit`. That is the Personales tab.
 * - `/api/environments*` — "which environments may I work in?", gated
 *   `p.env.use` on the project. That is the Del proyecto tab and the New-thread
 *   selector, which have to work for a PM who is not an administrator.
 *
 * Same shape as TeamsClient: a plain object over `adminFetch`, whose name is
 * historical — it is a JSON fetch with cookies and error unwrapping, and the
 * user-facing routes want exactly that too.
 *
 * The wire row is declared here instead of being reused from
 * `ProjectEnvironment`: the server's `toWire` deliberately drops the remembered
 * dev-server fields, and typing the client with the full contract would
 * promise the panel data the endpoint never sends.
 */
import type {
  EnvironmentCloneRequest,
  EnvironmentId,
  ProjectEnvironmentModificationKind,
  ProjectId,
  ProjectEnvironmentSyncState,
  ThreadId,
  UserId,
} from "@taskin-code/contracts";

import { adminFetch } from "../lib/adminFetch";

export interface AdminEnvironmentRow {
  readonly id: EnvironmentId;
  readonly projectId: ProjectId;
  /** Null for a project environment, which belongs to the project, not a person. */
  readonly userId: UserId | null;
  readonly kind: "personal" | "project";
  /** Only project environments are named (`qa`, `staging`, …). */
  readonly name: string | null;
  /** The branch a project environment follows, or null when it follows none. */
  readonly trackedBranch: string | null;
  readonly syncState: ProjectEnvironmentSyncState;
  readonly behindBy: number | null;
  readonly syncCheckedAt: string | null;
  readonly lastSyncedSha: string | null;
  readonly lastSyncAt: string | null;
  readonly lastSyncBy: UserId | null;
  /**
   * Who last wrote to the environment, from where, and what kind of write it
   * was — migration 062. Null for an environment nobody has changed yet, which
   * is every environment created before F2.
   */
  readonly lastModifiedAt: string | null;
  readonly lastModifiedBy: UserId | null;
  readonly lastModifiedThreadId: ThreadId | null;
  readonly lastModifiedKind: ProjectEnvironmentModificationKind | null;
  /**
   * The operation holding the environment's lock right now, if any.
   *
   * Read from the server's in-process mutex at the moment of the request, so it
   * is a fact with a short shelf life: it is what the card shows as
   * «Actualizando… por X», and it is refetched rather than remembered.
   */
  readonly activeOperation: {
    readonly operation: string;
    readonly threadId: ThreadId | null;
    readonly userId: UserId | null;
    readonly startedAt: string;
  } | null;
  /**
   * The pull request the environment is wearing right now, from migration 063.
   *
   * Preferred over `syncState` by everything that renders this row: while it is
   * set the environment is not its base branch, and "divergido" is a true but
   * useless thing to show a PM who is looking at a preview of PR #131.
   */
  readonly activeTrial: {
    readonly prNumber: number;
    readonly headSha: string;
    readonly startedAt: string;
    readonly startedBy: UserId | null;
    readonly threadId: ThreadId | null;
    readonly snapshots: ReadonlyArray<{
      readonly engine: string;
      readonly snapshotId: string;
      readonly label: string;
    }>;
  } | null;
  readonly clonedFromEnvironmentId: EnvironmentId | null;
  readonly clonedFromSha: string | null;
  readonly status: "provisioning" | "ready" | "failed";
  readonly branch: string;
  readonly worktreePath: string;
  readonly baseRef: string;
  readonly devHostname: string | null;
  readonly devPort: number | null;
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
}

export interface EnvironmentSyncStatus {
  readonly environmentId: EnvironmentId;
  readonly syncState: ProjectEnvironmentSyncState;
  readonly behindBy: number | null;
  readonly checkedAt: string;
  readonly trackedBranch: string | null;
  /** Commits the environment is missing, newest first. Capped by the server. */
  readonly pendingCommits: ReadonlyArray<{ readonly sha: string; readonly title: string }>;
  /** Set when the freshness could not be measured — an unreachable remote, mostly. */
  readonly error?: string | null;
}

/**
 * The messages the card's and the Pull Requests section's buttons pre-fill the
 * composer with, already rendered by the server: they mix the row, the
 * project's title and the project's own overrides in `.taskin/config.json`, and
 * only the server has all three together.
 */
export interface EnvironmentPromptSet {
  readonly sync: string;
  readonly resetData: string;
  readonly clone: string;
  /** Needs `prNumber` to read properly; without it the number says "(sin dato)". */
  readonly tryPr: string;
  readonly backToBase: string;
  /** The isolated pair (F4). `previewCreate` needs `prNumber` too. */
  readonly previewCreate: string;
  readonly previewDestroy: string;
}

/**
 * One isolated preview of a pull request, as the two surfaces read it.
 *
 * Declared here for the same reason `AdminEnvironmentRow` is: the endpoint adds
 * two fields the projection does not have — `url`, so nobody rebuilds it from
 * the hostname, and `activeOperation`, which is the in-process mutex and not a
 * column — and typing the client with `PrPreview` would both promise less and
 * more than the wire carries.
 *
 * There is no size: the row records a worktree path and a data source, not how
 * many bytes either of them holds, and measuring it would be a `du` per preview
 * on every render of the card.
 */
export interface PrPreviewRow {
  readonly id: string;
  readonly projectId: ProjectId;
  /** The project environment this preview was branched and copied from. */
  readonly baseEnvironmentId: EnvironmentId;
  readonly prNumber: number;
  readonly headSha: string | null;
  readonly baseSha: string;
  readonly status: "provisioning" | "ready" | "conflict" | "failed" | "destroyed";
  readonly branch: string;
  readonly worktreePath: string;
  readonly devHostname: string | null;
  readonly devPort: number | null;
  /** `snapshot:<id>`, `none`, or null while nothing has been copied yet. */
  readonly dataSource: string | null;
  /** Files `git merge` could not reconcile. Only in `conflict`. */
  readonly conflicts: ReadonlyArray<string> | null;
  readonly failureReason: string | null;
  readonly createdBy: UserId | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly refreshedAt: string | null;
  /** When the sweep will destroy it. Null while it has never reached `ready`. */
  readonly expiresAt: string | null;
  readonly destroyedAt: string | null;
  /** `https://<hostname>`, or null while it has no hostname. Built server-side. */
  readonly url: string | null;
  /**
   * Whoever holds this preview's mutex right now — building it, rebuilding it
   * or tearing it down. Keyed by the preview, so a sync of the base environment
   * never shows up here.
   */
  readonly activeOperation: {
    readonly operation: string;
    readonly threadId: ThreadId | null;
    readonly userId: UserId | null;
    readonly startedAt: string;
  } | null;
}

export interface AdminEnvironmentSnapshotRow {
  readonly snapshotId: string;
  readonly engine: string;
  readonly label: string;
  readonly sizeBytes: number | null;
  readonly createdAt: string;
}

export interface AdminEnvironmentSnapshots {
  readonly snapshots: ReadonlyArray<AdminEnvironmentSnapshotRow>;
  readonly totalBytes: number;
  /** Set when the snapshot store could not be read; the list is then empty. */
  readonly error: string | null;
}

export const EnvironmentsClient = {
  /** Admin view: every environment of a project, whoever owns it. Needs `u.view`. */
  async listByProject(projectId: ProjectId): Promise<ReadonlyArray<AdminEnvironmentRow>> {
    const result = await adminFetch<{ environments: ReadonlyArray<AdminEnvironmentRow> }>(
      `/api/admin/environments?projectId=${encodeURIComponent(projectId)}`,
    );
    return result.environments;
  },

  /**
   * User view: the caller's own personal environment plus every project one.
   * Needs `p.env.use` on the project and nothing else.
   */
  async listUsable(projectId: ProjectId): Promise<ReadonlyArray<AdminEnvironmentRow>> {
    const result = await adminFetch<{ environments: ReadonlyArray<AdminEnvironmentRow> }>(
      `/api/environments?projectId=${encodeURIComponent(projectId)}`,
    );
    return result.environments;
  },

  /**
   * Freshness, recomputed server-side: state, how far behind, and the titles of
   * the commits that are missing. Separate from the list because it runs a
   * `git fetch` per call, and only the open card pays for it.
   */
  async syncState(environmentId: EnvironmentId): Promise<EnvironmentSyncStatus> {
    const result = await adminFetch<{ syncState: EnvironmentSyncStatus }>(
      `/api/environments/${encodeURIComponent(environmentId)}/sync-state`,
    );
    return result.syncState;
  },

  /**
   * The pre-filled messages for this environment. Needs `p.env.use` — it is
   * text about an environment the caller can already see, and the buttons that
   * act on it carry their own permission.
   */
  async prompts(
    environmentId: EnvironmentId,
    options?: {
      snapshotLabel?: string | undefined;
      sourceName?: string | undefined;
      prNumber?: number | undefined;
    },
  ): Promise<EnvironmentPromptSet> {
    const query = new URLSearchParams();
    if (options?.snapshotLabel !== undefined && options.snapshotLabel.length > 0) {
      query.set("snapshotLabel", options.snapshotLabel);
    }
    if (options?.sourceName !== undefined && options.sourceName.length > 0) {
      query.set("sourceName", options.sourceName);
    }
    if (options?.prNumber !== undefined) {
      query.set("prNumber", String(options.prNumber));
    }
    const suffix = query.size === 0 ? "" : `?${query.toString()}`;
    const result = await adminFetch<{ prompts: EnvironmentPromptSet }>(
      `/api/environments/${encodeURIComponent(environmentId)}/prompts${suffix}`,
    );
    return result.prompts;
  },

  async provision(input: {
    projectId: ProjectId;
    userId: UserId;
    baseBranch?: string;
  }): Promise<AdminEnvironmentRow> {
    const result = await adminFetch<{ environment: AdminEnvironmentRow }>(
      "/api/admin/environments",
      {
        method: "POST",
        body: JSON.stringify({
          projectId: input.projectId,
          kind: "personal",
          userId: input.userId,
          ...(input.baseBranch !== undefined && input.baseBranch.length > 0
            ? { baseBranch: input.baseBranch }
            : {}),
        }),
      },
    );
    return result.environment;
  },

  /**
   * The shared environment of a project. Needs `p.env.manage`, and the project
   * needs a base branch — the server refuses otherwise rather than guessing one.
   */
  async provisionProject(input: {
    projectId: ProjectId;
    name: string;
    cloneFrom?: EnvironmentCloneRequest | undefined;
  }): Promise<AdminEnvironmentRow> {
    const result = await adminFetch<{ environment: AdminEnvironmentRow }>(
      "/api/admin/environments",
      {
        method: "POST",
        body: JSON.stringify({
          projectId: input.projectId,
          kind: "project",
          name: input.name,
          ...(input.cloneFrom !== undefined ? { cloneFrom: input.cloneFrom } : {}),
        }),
      },
    );
    return result.environment;
  },

  async destroy(environmentId: EnvironmentId): Promise<void> {
    await adminFetch<{ ok: true }>(`/api/admin/environments/${encodeURIComponent(environmentId)}`, {
      method: "DELETE",
    });
  },

  /**
   * The project's isolated previews, live and destroyed, oldest first.
   *
   * Gated on `p.env.use`, not on `p.pr.preview`: a preview belongs to the
   * project, and a reviewer who was handed its URL in a comment has to be able
   * to find it. Acting on one is what needs the stronger grant.
   */
  async listPrPreviews(projectId: ProjectId): Promise<ReadonlyArray<PrPreviewRow>> {
    const result = await adminFetch<{ previews: ReadonlyArray<PrPreviewRow> }>(
      `/api/projects/${encodeURIComponent(projectId)}/pr-previews`,
    );
    return result.previews;
  },

  /**
   * Push the expiry out. Needs `p.pr.preview` on the preview's own project.
   *
   * `hours` is counted from whichever is later, now or the expiry it already
   * had, and the server refuses anything that is not a whole number of hours
   * between 1 and 168.
   */
  async extendPrPreview(previewId: string, hours: number): Promise<PrPreviewRow> {
    const result = await adminFetch<{ preview: PrPreviewRow }>(
      `/api/pr-previews/${encodeURIComponent(previewId)}/extend`,
      { method: "POST", body: JSON.stringify({ hours }) },
    );
    return result.preview;
  },

  /**
   * Tear a preview down. Needs `p.pr.preview` on the preview's own project.
   *
   * Answers 202 rather than 204: the row is marked destroyed before the
   * worktree and the databases are actually gone, so the caller should refetch
   * on the domain event rather than assume the disk is free.
   */
  async destroyPrPreview(previewId: string): Promise<void> {
    await adminFetch<{ ok: true }>(`/api/pr-previews/${encodeURIComponent(previewId)}`, {
      method: "DELETE",
    });
  },

  /** Admin view of an environment's copies, for the Personales table. Needs `u.view`. */
  async snapshots(environmentId: EnvironmentId): Promise<AdminEnvironmentSnapshots> {
    return await adminFetch<AdminEnvironmentSnapshots>(
      `/api/admin/environments/${encodeURIComponent(environmentId)}/snapshots`,
    );
  },

  /**
   * The same list, gated on `p.env.use` instead of `u.view`.
   *
   * The «Resetear datos» menu is pressed by a PM with `p.env.sync` and no
   * administrator role: reading the copies through the admin route would have
   * left them a menu that could only ever offer the baseline.
   */
  async usableSnapshots(environmentId: EnvironmentId): Promise<AdminEnvironmentSnapshots> {
    return await adminFetch<AdminEnvironmentSnapshots>(
      `/api/environments/${encodeURIComponent(environmentId)}/snapshots`,
    );
  },
};
